import { isNull } from "lodash-es";
import { matchPath } from "react-router";
import {
  Activity,
  AudioLines,
  BarChart3,
  Bot,
  Briefcase,
  Award,
  Clock,
  DollarSign,
  Download,
  FileText,
  FolderKanban,
  Globe,
  Home,
  Layers,
  Mail,
  Brain,
  Rocket,
  Scissors,
  Settings,
  Upload,
  User,
  Users,
  Mic,
} from "lucide-react";

import { atlasRoutes } from "@/app/router/routes";
import {
  SidebarGroupId,
  SidebarItemKind,
  SidebarSectionId,
  SidebarSectionItem,
} from "@/app/layout/types";

export const buildSidebarSections = (pathname: string): SidebarSectionItem[] => [
  {
    kind: SidebarItemKind.Section,
    label: "Overview",
    id: SidebarSectionId.Overview,
    items: [
      {
        kind: SidebarItemKind.Button,
        label: "Home",
        icon: Home,
        href: atlasRoutes.home({}).path,
        isActive: !isNull(matchPath(pathname, atlasRoutes.home({}).path)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Settings",
        icon: Settings,
        href: atlasRoutes.settings({}).path,
        isActive: !isNull(matchPath(pathname, atlasRoutes.settings({}).path)),
      },
    ],
  },
  {
    kind: SidebarItemKind.Section,
    label: "Support",
    id: SidebarSectionId.Support,
    items: [
      {
        kind: SidebarItemKind.Button,
        label: "SpaceAccounts",
        icon: Rocket,
        href: atlasRoutes.spaceAccounts({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.spaceAccounts({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Sessions",
        icon: Clock,
        href: atlasRoutes.sessions({}).path,
        isActive: !isNull(matchPath(atlasRoutes.sessions({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Assistants",
        icon: Bot,
        href: atlasRoutes.assistants({}).path,
        isActive: !isNull(matchPath(atlasRoutes.assistants({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Billing",
        icon: DollarSign,
        href: atlasRoutes.billing({}).path,
        isActive: !isNull(matchPath(atlasRoutes.billing({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Notes",
        icon: FileText,
        href: atlasRoutes.notes({}).path,
        isActive: !isNull(matchPath(atlasRoutes.notes({ starPattern: true }).path, pathname)),
      },
    ],
  },
  {
    kind: SidebarItemKind.Section,
    label: "Utilities",
    id: SidebarSectionId.Utilities,
    items: [
      {
        kind: SidebarItemKind.Button,
        label: "Accounts",
        icon: Users,
        href: atlasRoutes.accounts({}).path,
        isActive: !isNull(matchPath(atlasRoutes.accounts({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Collections",
        icon: FolderKanban,
        href: atlasRoutes.collections({}).path,
        isActive: !isNull(matchPath(atlasRoutes.collections({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Legacy Firestore Users",
        icon: Users,
        href: atlasRoutes.legacyFirestoreUsers({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.legacyFirestoreUsers({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Group,
        id: SidebarGroupId.UtilitiesImportsExports,
        label: "Imports & Exports",
        icon: Download,
        items: [
          {
            kind: SidebarItemKind.Button,
            label: "Imports",
            icon: Download,
            href: atlasRoutes.imports({}).path,
            isActive: !isNull(matchPath(atlasRoutes.imports({ starPattern: true }).path, pathname)),
          },
          {
            kind: SidebarItemKind.Button,
            label: "Exports",
            icon: Upload,
            href: atlasRoutes.dataExports({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.dataExports({ starPattern: true }).path, pathname)
            ),
          },
        ],
      },
      {
        kind: SidebarItemKind.Group,
        id: SidebarGroupId.UtilitiesWebClipper,
        label: "Web Clipper",
        icon: Layers,
        items: [
          {
            kind: SidebarItemKind.Button,
            label: "Uploaded Files",
            icon: Upload,
            href: atlasRoutes.uploadedFiles({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.uploadedFiles({ starPattern: true }).path, pathname)
            ),
          },
          {
            kind: SidebarItemKind.Button,
            label: "Web Clips",
            icon: Scissors,
            href: atlasRoutes.webClips({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.webClips({ starPattern: true }).path, pathname)
            ),
          },
          {
            kind: SidebarItemKind.Button,
            label: "Captured Websites",
            icon: Globe,
            href: atlasRoutes.capturedWebsites({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.capturedWebsites({ starPattern: true }).path, pathname)
            ),
          },
        ],
      },
      {
        kind: SidebarItemKind.Button,
        label: "Captured Audio",
        icon: AudioLines,
        href: atlasRoutes.capturedAudio({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.capturedAudio({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Audio Recordings",
        icon: Mic,
        href: atlasRoutes.audioRecordings({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.audioRecordings({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Captured Emails",
        icon: Mail,
        href: atlasRoutes.capturedEmails({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.capturedEmails({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Shared Item Invites",
        icon: Users,
        href: atlasRoutes.sharedItemInvites({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.sharedItemInvites({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "App Events",
        icon: Activity,
        href: atlasRoutes.appEvents({}).path,
        isActive: !isNull(matchPath(atlasRoutes.appEvents({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Group,
        id: SidebarGroupId.UtilitiesSmart,
        label: "Smart",
        icon: Brain,
        items: [
          {
            kind: SidebarItemKind.Button,
            label: "Smart Views",
            icon: Settings,
            href: atlasRoutes.smartViews({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.smartViews({ starPattern: true }).path, pathname)
            ),
          },
          {
            kind: SidebarItemKind.Button,
            label: "Smart Engine Runs",
            icon: Settings,
            href: atlasRoutes.smartEngineRuns({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.smartEngineRuns({ starPattern: true }).path, pathname)
            ),
          },
        ],
      },
      {
        kind: SidebarItemKind.Button,
        label: "API Request Records",
        icon: Settings,
        href: atlasRoutes.apiRequestRecords({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.apiRequestRecords({ starPattern: true }).path, pathname)
        ),
      },
    ],
  },
  {
    kind: SidebarItemKind.Section,
    label: "Sync",
    id: SidebarSectionId.Sync,
    items: [
      {
        kind: SidebarItemKind.Button,
        label: "SyncClientGroups",
        icon: Settings,
        href: atlasRoutes.syncClientGroups({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.syncClientGroups({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "SyncModelCvrSnapshots",
        icon: Settings,
        href: atlasRoutes.syncModelCvrSnapshots({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.syncModelCvrSnapshots({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "SyncModelEvents",
        icon: Settings,
        href: atlasRoutes.syncModelEvents({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.syncModelEvents({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "SyncScopes",
        icon: Settings,
        href: atlasRoutes.syncScopes({}).path,
        isActive: !isNull(matchPath(atlasRoutes.syncScopes({ starPattern: true }).path, pathname)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "SyncOperations",
        icon: Settings,
        href: atlasRoutes.syncOperations({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.syncOperations({ starPattern: true }).path, pathname)
        ),
      },
    ],
  },
  {
    kind: SidebarItemKind.Section,
    label: "Company",
    id: SidebarSectionId.Company,
    items: [
      {
        kind: SidebarItemKind.Button,
        label: "Team",
        icon: Briefcase,
        href: atlasRoutes.companyTeam({}).path,
        isActive: !isNull(matchPath(pathname, atlasRoutes.companyTeam({}).path)),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Profile",
        icon: User,
        href: atlasRoutes.companyProfile({}).path,
        isActive: !isNull(matchPath(pathname, atlasRoutes.companyProfile({}).path)),
      },
    ],
  },
  {
    kind: SidebarItemKind.Section,
    label: "Admin",
    id: SidebarSectionId.Admin,
    items: [
      {
        kind: SidebarItemKind.Group,
        id: SidebarGroupId.AdminBadges,
        label: "Badges",
        icon: Award,
        items: [
          {
            kind: SidebarItemKind.Button,
            label: "Badges",
            icon: Settings,
            href: atlasRoutes.adminBadges({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.adminBadges({ starPattern: true }).path, pathname)
            ),
          },
          {
            kind: SidebarItemKind.Button,
            label: "Member Badges",
            icon: Settings,
            href: atlasRoutes.adminMemberBadges({}).path,
            isActive: !isNull(
              matchPath(atlasRoutes.adminMemberBadges({ starPattern: true }).path, pathname)
            ),
          },
        ],
      },
      {
        kind: SidebarItemKind.Button,
        label: "Linear",
        icon: BarChart3,
        href: atlasRoutes.adminLinearDashboard({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.adminLinearDashboard({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Audio Recording Quality",
        icon: Mic,
        href: atlasRoutes.audioRecordingsQualityMetrics({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.audioRecordingsQualityMetrics({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "V1 Batch Account Upgrades",
        icon: Settings,
        href: atlasRoutes.batchAccountUpgrades({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.batchAccountUpgrades({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Legacy Firestore Users",
        icon: Users,
        href: atlasRoutes.legacyFirestoreUsers({}).path,
        isActive: !isNull(
          matchPath(atlasRoutes.legacyFirestoreUsers({ starPattern: true }).path, pathname)
        ),
      },
      {
        kind: SidebarItemKind.Button,
        label: "Monetization",
        icon: DollarSign,
        href: atlasRoutes.monetization({}).path,
        isActive: !isNull(matchPath(pathname, atlasRoutes.monetization({}).path)),
      },
    ],
  },
];
